import { Avatar, Button, Card, Select, Space, Table, Tag } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import type { SorterResult } from 'antd/es/table/interface'
import { PropertyType, PtoV2ItemType, ResponsePagingType, SearchPTOParams } from 'Modules'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import ButtonCopy from 'component/button/button-copy'
import LinkAddress from 'component/link/link-address'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { FEATURE } from 'constants/feature'
import { DEFAULT_PAGE_SIZE } from 'constants/pagination'
import { PTO_TYPE_OPTIONS, PTOTypeEnum } from 'constants/pto'
import { SELLING_STATUS_CONFIG, SellingStatusEnum } from 'constants/pto-v2'
import { usePermission } from 'hooks/use-permission'
import useSearchParams from 'hooks/use-search-params'
import { PAGES } from 'routes'
import { logError } from 'utils/logger'

import ButtonDeployContract from './button-deploy-contract'
import Overview from './overview'

import './style.scss'

const SORT_ORDER_MAP: Record<string, string> = {
  ascend: 'asc',
  descend: 'desc',
}

/**
 * PTO v2 list: adds Contract Name & Selling Status columns, Status becomes
 * Display (Show/Hide), lazy mint (no pre-mint action / mint status column).
 */
const PTOV2 = () => {
  const [filteredData, setFilteredData] = useState<PtoV2ItemType[]>([])
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>([])
  const [total, setTotal] = useState(0)
  const [searchValue, setSearchValue] = useState('')

  const navigate = useNavigate()

  const additionalItems = [BREADCRUMBS.PTO_V2]

  const { canCRUD } = usePermission()

  const { searchParams, setSearchParams, initPagination, onChangePagination, onSearch } =
    useSearchParams<SearchPTOParams>()

  const disableButton = useMemo(() => {
    return !canCRUD(FEATURE.PTO)
  }, [canCRUD])

  const getData = useCallback(async (params?: SearchPTOParams) => {
    try {
      const res = await AppAdmin.getListPTOV2(params)

      if (res?.data.success) {
        const { results, totalResults } =
          res.data?.data || ({} as ResponsePagingType<PtoV2ItemType>)
        setTotal(totalResults || 0)
        setFilteredData(results)
      }
    } catch (error) {
      setTotal(0)
      setFilteredData([])
      logError('Error: ', error)
    }
  }, [])

  const getListPropertyTypes = async () => {
    try {
      const res = await AppAdmin.getListProperty()
      if (res?.data.success) {
        setPropertyTypes(
          res?.data.data.map((item: PropertyType) => ({
            ...item,
            id: Number(item.id),
          }))
        )
      }
    } catch (error) {
      logError('Error: ', error)
    }
  }

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: any,
    sorter: SorterResult<PtoV2ItemType> | SorterResult<PtoV2ItemType>[]
  ) => {
    onChangePagination(pagination)

    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter
    const sort =
      singleSorter.field && singleSorter.order
        ? `${singleSorter.field}:${SORT_ORDER_MAP[singleSorter.order]}`
        : undefined

    setSearchParams((prev) => ({ ...prev, sort }))
  }

  useEffect(() => {
    getData(searchParams)
  }, [searchParams, getData])

  useEffect(() => {
    getListPropertyTypes()
  }, [])

  const columns: ColumnsType<PtoV2ItemType> = [
    {
      title: 'Logo',
      dataIndex: 'thumbnail',
      key: 'thumbnail',
      render: (logo) => <Avatar src={logo} size={50} />,
    },
    {
      title: 'Name',
      dataIndex: 'fullName',
      key: 'fullName',
      sorter: true,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      sorter: true,
      render: (text) => {
        const typeObj = propertyTypes.find((type) => type.id === text)
        return typeObj ? typeObj.name : text
      },
    },
    {
      title: 'PTO Type',
      dataIndex: 'ptoType',
      key: 'ptoType',
      sorter: true,
      render: (text) => {
        if (text === PTOTypeEnum.RENTAL_PTO) return <Tag color="purple">Rental</Tag>
        return <Tag color="blue">PTO</Tag>
      },
    },
    {
      title: 'Contract Name',
      dataIndex: 'contractName',
      key: 'contractName',
      render: (text) => text || '-',
    },
    {
      title: 'Contract Address',
      dataIndex: 'contractAddress',
      key: 'contractAddress',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LinkAddress address={text} />
          {text ? <ButtonCopy text={text} /> : null}
        </div>
      ),
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      sorter: true,
    },
    {
      title: 'Display',
      dataIndex: 'display',
      key: 'display',
      render: (display?: string) =>
        `${display || ''}`.toLowerCase() === 'show' ? (
          <span className="status-success">Show</span>
        ) : (
          <span className="status-fail">Hide</span>
        ),
    },
    {
      title: 'Selling Status',
      dataIndex: 'sellingStatus',
      key: 'sellingStatus',
      render: (sellingStatus?: SellingStatusEnum) => {
        const config =
          SELLING_STATUS_CONFIG[sellingStatus ?? SellingStatusEnum.OFF_SALE] ||
          SELLING_STATUS_CONFIG[SellingStatusEnum.OFF_SALE]
        return <Tag color={config.color}>{config.label}</Tag>
      },
    },
    {
      title: 'Total Minted',
      dataIndex: 'mint',
      key: 'mint',
      render: (_, record) => `${record.mint ?? 0} / ${record.total ?? 0}`,
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          {!record.contractAddress ? (
            <ButtonDeployContract id={record._id} onSuccess={() => getData(searchParams)} />
          ) : null}
          <Button
            className="button-detail"
            onClick={() => {
              navigate(`${PAGES.PTO_V2}/${record._id}`)
            }}
          >
            View
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div className="main-section">
      <Breadcrumb additionalItems={additionalItems} />
      <Overview />
      <div className="main-title">PTO v2 Admin</div>
      <div className="table-row">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="p2p-search">
            <input
              type="text"
              placeholder="Search by name or contract address..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onSearch({ keyword: searchValue } as SearchPTOParams)
                }
              }}
            />
            <Select
              allowClear
              placeholder="All Types"
              value={searchParams.ptoType}
              onChange={(value) => onSearch({ ptoType: value } as SearchPTOParams)}
              options={PTO_TYPE_OPTIONS}
              style={{ width: 120 }}
            />
            <Button
              type="primary"
              onClick={() => onSearch({ keyword: searchValue } as SearchPTOParams)}
            >
              Search
            </Button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Button
              disabled={disableButton}
              type="primary"
              onClick={() => {
                navigate(PAGES.PTO_V2_CREATE)
              }}
            >
              Create
            </Button>
          </div>
        </div>
        <Card>
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey={(record) => record._id}
            pagination={total <= DEFAULT_PAGE_SIZE ? false : initPagination(total)}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}
          />
        </Card>
      </div>
    </div>
  )
}

export default PTOV2
